import { useEffect, useState } from "react";
import { fetchActiveDocument } from "src/data/legalDocument";
import { captureError } from "src/utils/errorReporting";
import { useLang } from "src/i18n";

/**
 * Fetch the active version of a legal document type on mount (public route;
 * the lookup is tenant-blind — the typeName carries the tenant), and again
 * whenever the active UI language changes (backend falls back to the Finnish
 * row when no translation is published yet).
 * Returns `{ doc, status }` where status is "loading" | "ready" | "error".
 * Errors are reported to Sentry and surfaced as status="error" for the caller
 * to render. For the silent-fallback acceptance-copy use case (wizard / offer
 * microcopy) use `useAcceptanceCopy` instead — it has a different contract.
 */
export function useActiveLegalDocument(typeName) {
    const { lang } = useLang();
    const [doc, setDoc] = useState(null);
    const [status, setStatus] = useState("loading");
    useEffect(() => {
        let cancelled = false;
        fetchActiveDocument(typeName, { language: lang })
            .then((row) => { if (!cancelled) { setDoc(row); setStatus("ready"); } })
            .catch((err) => {
                if (!cancelled) setStatus("error");
                captureError(err, { tags: { entity: "legalDocument", typeName, swallowed: true } });
            });
        return () => { cancelled = true; };
    }, [typeName, lang]);
    return { doc, status };
}
